import { type Position } from "./types"
import { Renderer } from "./renderer"

export type InputAction =
  | { type: "click_grid"; pos: Position }
  | { type: "rotate" }
  | { type: "hover_grid"; pos: Position | null }

export type InputCallback = (action: InputAction) => void

export class InputHandler {
  private canvas: HTMLCanvasElement
  private renderer: Renderer
  private callback: InputCallback
  private gridSize: number
  private boundHandlers: { type: string; handler: EventListener }[] = []

  constructor(canvas: HTMLCanvasElement, renderer: Renderer, gridSize: number, callback: InputCallback) {
    this.canvas = canvas
    this.renderer = renderer
    this.gridSize = gridSize
    this.callback = callback
    this.bind()
  }

  private bind(): void {
    const onClick = (e: MouseEvent) => {
      const pos = this.eventToGrid(e)
      if (pos) this.callback({ type: "click_grid", pos })
    }

    const onMouseMove = (e: MouseEvent) => {
      const pos = this.eventToGrid(e)
      this.callback({ type: "hover_grid", pos })
    }

    const onMouseLeave = () => {
      this.callback({ type: "hover_grid", pos: null })
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "r" || e.key === "R") {
        e.preventDefault()
        this.callback({ type: "rotate" })
      }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (e.changedTouches.length > 0) {
        const touch = e.changedTouches[0]
        const rect = this.canvas.getBoundingClientRect()
        const pos = this.renderer.pixelToGrid(
          touch.clientX - rect.left,
          touch.clientY - rect.top,
        )
        if (pos && pos.row >= 0 && pos.row < this.gridSize && pos.col >= 0 && pos.col < this.gridSize) {
          e.preventDefault()
          this.callback({ type: "click_grid", pos })
        }
      }
    }

    this.canvas.addEventListener("click", onClick as EventListener)
    this.canvas.addEventListener("mousemove", onMouseMove as EventListener)
    this.canvas.addEventListener("mouseleave", onMouseLeave as EventListener)
    this.canvas.addEventListener("touchend", onTouchEnd as EventListener, { passive: false })
    document.addEventListener("keydown", onKeyDown as EventListener)

    this.boundHandlers = [
      { type: "click", handler: onClick as EventListener },
      { type: "mousemove", handler: onMouseMove as EventListener },
      { type: "mouseleave", handler: onMouseLeave as EventListener },
      { type: "touchend", handler: onTouchEnd as EventListener },
    ]
    // Store keydown separately for cleanup on document
    this.boundHandlers.push({ type: "keydown", handler: onKeyDown as EventListener })
  }

  private eventToGrid(e: MouseEvent): Position | null {
    const rect = this.canvas.getBoundingClientRect()
    const pos = this.renderer.pixelToGrid(e.clientX - rect.left, e.clientY - rect.top)
    if (!pos) return null
    if (pos.row < 0 || pos.row >= this.gridSize || pos.col < 0 || pos.col >= this.gridSize) return null
    return pos
  }

  destroy(): void {
    for (const { type, handler } of this.boundHandlers) {
      if (type === "keydown") {
        document.removeEventListener(type, handler)
      } else {
        this.canvas.removeEventListener(type, handler)
      }
    }
    this.boundHandlers = []
  }
}
